import classNames from "classnames";
import React from "react";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { useMountEffect } from "../../../hooks/useMountEffect";
import {
    CountdownTimerTheme,
    ICountdownTime,
    ICountdownTimer,
} from "../../../models/countdown-timer";
import { BreakPoint } from "../../../models/screen";
import { getViewportBreakpoint } from "../../../utils/detectMobile";

import styles from "./CountdownTimer.module.scss";

function formatNumber(n: number | null): string {
    if (n === null) {
        return "";
    }

    // TODO(i18n): Hardcoded locale "en-US" - should use current user's locale or site locale
    return n.toLocaleString("en-US", {
        minimumIntegerDigits: 2,
        useGrouping: false,
    });
}

function calcTimeDiff(
    currentTimer: ICountdownTimer | null,
    onTimerExpired: () => void,
): ICountdownTime | null {
    const now = new Date().getTime();
    let end = 0;
    if (currentTimer) {
        end = Date.parse(currentTimer.end_date);
    }
    const difference = end - now;

    if (difference > 0) {
        const timeLeft: ICountdownTime = {
            days: Math.floor(difference / (1000 * 60 * 60 * 24)),
            hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
            minutes: Math.floor((difference / 1000 / 60) % 60),
            seconds: Math.floor((difference / 1000) % 60),
        };
        return timeLeft;
    } else {
        onTimerExpired();
        return null;
    }
}

function getBodyCopy(currentTimer: ICountdownTimer | null): string {
    if (currentTimer) {
        let body = currentTimer.body;
        if (
            getViewportBreakpoint() < BreakPoint.SMALL &&
            currentTimer.mobile_body
        ) {
            body = currentTimer.mobile_body;
        }
        return body;
    }
    return "";
}

function getThemeStyle(timerTheme: CountdownTimerTheme | undefined): string {
    switch (timerTheme) {
        case CountdownTimerTheme.RED:
            return styles.red;
        case CountdownTimerTheme.YELLOW:
            return styles.yellow;
        case CountdownTimerTheme.CARDS:
            return styles.cards;
        default:
            return "";
    }
}

export const CountdownTimer = (props: {
    currentTimer: ICountdownTimer | null;
    onTimerExpired: () => void;
}): React.ReactNode => {
    const [timeLeft, setTimeLeft] = React.useState<ICountdownTime | null>({
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
    });

    useMountEffect(() => {
        const window = getWindow();
        if (!window) return;

        const timerID = window.setInterval(() => {
            setTimeLeft(calcTimeDiff(props.currentTimer, props.onTimerExpired));
        }, 1000);

        return () => {
            clearInterval(timerID);
        };
    });

    if (!timeLeft) {
        return <></>;
    }

    const formattedDays = formatNumber(timeLeft?.days);
    const formattedHours = formatNumber(timeLeft?.hours);
    const formattedMin = formatNumber(timeLeft?.minutes);
    const formattedSec = formatNumber(timeLeft?.seconds);
    const hasTime =
        formattedDays !== "00" ||
        formattedHours !== "00" ||
        formattedMin !== "00" ||
        formattedSec !== "00";

    const themeStyle = getThemeStyle(props.currentTimer?.theme);

    const timerClasses = classNames({
        [styles.countdownTimer]: true,
        [themeStyle]: true,
    });

    // TODO : Use better semnatic markup for date layout below. Table?
    return (
        <>
            {/* Added condition "hasTime" so that this promo copy is rendered at the same time as the countdown timer. */}
            {hasTime && props.currentTimer && (
                <div
                    className={styles.content}
                    dangerouslySetInnerHTML={{
                        __html: getBodyCopy(props.currentTimer),
                    }}
                ></div>
            )}
            {hasTime && (
                <div className={timerClasses}>
                    <div>
                        {/* TODO(i18n): Wrap in t tagged template: {t`Sale`} and {t`Ends In`} */}
                        <h2>
                            <span className="countdown-timer__copy--trim">
                                Sale <br />
                            </span>
                            Ends In
                        </h2>
                    </div>
                    <div>
                        <div>
                            <div>
                                <span>{formattedDays}</span>
                                {/* TODO(i18n): Use ngettext for plurals: {ngettext(msgid`${formattedDays} day`, `${formattedDays} days`, timeLeft.days)} */}
                                <span>days</span>
                            </div>
                            <div className={`${styles.colon} colon`}>:</div>
                        </div>
                        <div>
                            <div>
                                <span>{formattedHours}</span>
                                {/* TODO(i18n): Use ngettext for plurals and translate complete word: {ngettext(msgid`${formattedHours} hour`, `${formattedHours} hours`, timeLeft.hours)} - don't split "hours" into fragments */}
                                <span>
                                    h
                                    <span className="countdown-timer__copy--trim">
                                        ou
                                    </span>
                                    rs
                                </span>
                            </div>
                            <div className={`${styles.colon} colon`}>:</div>
                        </div>
                        <div>
                            <div>
                                <span>{formattedMin}</span>
                                {/* TODO(i18n): Use ngettext for plurals: {ngettext(msgid`${formattedMin} minute`, `${formattedMin} minutes`, timeLeft.minutes)} */}
                                <span>min</span>
                            </div>
                            <div className={`${styles.colon} colon`}>:</div>
                        </div>
                        <div>
                            <div>
                                <span>{formattedSec}</span>
                                {/* TODO(i18n): Use ngettext for plurals: {ngettext(msgid`${formattedSec} second`, `${formattedSec} seconds`, timeLeft.seconds)} */}
                                <span>sec</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
